import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Users,
  TrendingDown,
  PieChart,
  Calendar,
  FileX,
  Clock,
  Package,
  Download,
  RefreshCw,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import { LoadingState } from '../../../components/AsyncStates';
import {
  getHeadcountTrends,
  getDemographics,
  getTurnoverByDepartment,
  getProbationTracking,
  getAssetAllocation,
  getDocumentExpiry,
  getContractExpiry,
  exportReport,
  HeadcountTrendView,
  DemographicsView,
  TurnoverByDepartmentView,
  ProbationTrackingView,
  AssetAllocationView,
  DocumentExpiryView,
  ContractExpiryView,
} from '../../../api/General/hrAnalytics';
import { getDepartments } from '../../../api/General/Company';
import type { Department } from '../../../models/general';
import { useSelector } from 'react-redux';
import { todayLocalDateParam } from '@/utils/apiDate';

interface ReportType {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  bgColor: string;
}

const reportTypes: ReportType[] = [
  { id: 'headcount', title: 'hrAnalytics.headcountAnalysis', icon: <Users className="w-5 h-5" />, description: 'hrAnalytics.headcountDescription', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { id: 'turnover', title: 'hrAnalytics.turnoverAnalytics', icon: <TrendingDown className="w-5 h-5" />, description: 'hrAnalytics.turnoverDescription', color: 'text-red-600', bgColor: 'bg-red-50' },
  { id: 'demographics', title: 'hrAnalytics.demographicsOverview', icon: <PieChart className="w-5 h-5" />, description: 'hrAnalytics.demographicsDescription', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  { id: 'contracts', title: 'hrAnalytics.contractExpiryTitle', icon: <Calendar className="w-5 h-5" />, description: 'hrAnalytics.contractExpiryDescription', color: 'text-orange-600', bgColor: 'bg-orange-50' },
  { id: 'documents', title: 'hrAnalytics.documentExpiryTitle', icon: <FileX className="w-5 h-5" />, description: 'hrAnalytics.documentExpiryDescription', color: 'text-pink-600', bgColor: 'bg-pink-50' },
  { id: 'probation', title: 'hrAnalytics.probationTracking', icon: <Clock className="w-5 h-5" />, description: 'hrAnalytics.probationDescription', color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  { id: 'assets', title: 'hrAnalytics.assetAllocationTitle', icon: <Package className="w-5 h-5" />, description: 'hrAnalytics.assetAllocationDescription', color: 'text-teal-600', bgColor: 'bg-teal-50' },
];

export default function CoreHRReports() {
  const { t } = useTranslation();
  const companyId = useSelector((state: { user?: { company?: { id?: number } } }) => state.user?.company?.id);
  const [selectedReport, setSelectedReport] = useState('headcount');
  const [isExporting, setIsExporting] = useState(false);
  const [headcountTrends, setHeadcountTrends] = useState<HeadcountTrendView[]>([]);
  const [demographics, setDemographics] = useState<DemographicsView | null>(null);
  const [turnoverData, setTurnoverData] = useState<TurnoverByDepartmentView[]>([]);
  const [probationData, setProbationData] = useState<ProbationTrackingView | null>(null);
  const [assetData, setAssetData] = useState<AssetAllocationView[]>([]);
  const [documentData, setDocumentData] = useState<DocumentExpiryView | null>(null);
  const [contractData, setContractData] = useState<ContractExpiryView | null>(null);
  const [companyDepartments, setCompanyDepartments] = useState<Department[]>([]);
  const [loadingHeadcount, setLoadingHeadcount] = useState(false);
  const [loadingDemographics, setLoadingDemographics] = useState(false);
  const [loadingTurnover, setLoadingTurnover] = useState(false);
  const [loadingProbation, setLoadingProbation] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);

  // Fetch departments for filter dropdown
  useEffect(() => {
    if (!companyId) return;
    getDepartments(companyId).then(result => {
      if (result.errors.length === 0) {
        setCompanyDepartments(result.payload);
      }
    }).catch(() => { /* silently fail */ });
  }, [companyId]);

  const fetchHeadcount = useCallback(async () => {
    setLoadingHeadcount(true);
    try {
      const result = await getHeadcountTrends(selectedMonths, selectedDepartmentId);
      if (result.errors.length === 0) {
        setHeadcountTrends(result.payload);
      }
    } catch {
      // Silently fail — data will remain empty
    } finally {
      setLoadingHeadcount(false);
    }
  }, [selectedMonths, selectedDepartmentId]);

  const fetchDemographics = useCallback(async () => {
    setLoadingDemographics(true);
    try {
      const result = await getDemographics(selectedDepartmentId);
      if (result.errors.length === 0) {
        setDemographics(result.payload);
      }
    } catch {
      // Silently fail — data will remain empty
    } finally {
      setLoadingDemographics(false);
    }
  }, [selectedDepartmentId]);

  const fetchTurnover = useCallback(async () => {
    setLoadingTurnover(true);
    try {
      const result = await getTurnoverByDepartment(selectedMonths, selectedDepartmentId);
      if (result.errors.length === 0) {
        setTurnoverData(result.payload);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingTurnover(false);
    }
  }, [selectedMonths, selectedDepartmentId]);

  const fetchProbation = useCallback(async () => {
    setLoadingProbation(true);
    try {
      const result = await getProbationTracking(selectedDepartmentId);
      if (result.errors.length === 0) {
        setProbationData(result.payload);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingProbation(false);
    }
  }, [selectedDepartmentId]);

  const fetchAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const result = await getAssetAllocation(selectedDepartmentId);
      if (result.errors.length === 0) {
        setAssetData(result.payload);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingAssets(false);
    }
  }, [selectedDepartmentId]);

  const fetchDocuments = useCallback(async () => {
    setLoadingDocuments(true);
    try {
      const result = await getDocumentExpiry(selectedDepartmentId);
      if (result.errors.length === 0) {
        setDocumentData(result.payload);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingDocuments(false);
    }
  }, [selectedDepartmentId]);

  const fetchContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const result = await getContractExpiry(selectedDepartmentId);
      if (result.errors.length === 0) {
        setContractData(result.payload);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingContracts(false);
    }
  }, [selectedDepartmentId]);

  useEffect(() => {
    fetchHeadcount();
    fetchDemographics();
    fetchTurnover();
    fetchProbation();
    fetchAssets();
    fetchDocuments();
    fetchContracts();
  }, [fetchHeadcount, fetchDemographics, fetchTurnover, fetchProbation, fetchAssets, fetchDocuments, fetchContracts]);

  const refreshAll = () => {
    fetchHeadcount();
    fetchDemographics();
    fetchTurnover();
    fetchProbation();
    fetchAssets();
    fetchDocuments();
    fetchContracts();
  };

  const handleExport = async (format: 'pdf' | 'xlsx') => {
    setIsExporting(true);
    try {
      const blob = await exportReport(selectedReport, format, selectedMonths, selectedDepartmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HR-${selectedReport}-${todayLocalDateParam()}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      // Could show error toast
    } finally {
      setIsExporting(false);
    }
  };

  const renderReportContent = () => {
    switch (selectedReport) {
      case 'headcount': {
        if (loadingHeadcount) return <LoadingState title={t('hrAnalytics.loading')} size="md" />;
        const latestMonth = headcountTrends.length > 0 ? headcountTrends[headcountTrends.length - 1] : null;
        const maxEmployees = Math.max(...headcountTrends.map((h) => h.totalEmployees), 1);
        const minEmployees = Math.min(...headcountTrends.map((h) => h.totalEmployees), 0);
        const range = maxEmployees - minEmployees || 1;
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('hrAnalytics.headcountTrends')}</h3>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-600">{t('hrAnalytics.employeeCount')}</span>
              </div>
            </div>
            {headcountTrends.length === 0 ? (
              <div className="text-center py-12 text-gray-500">{t('hrAnalytics.noData')}</div>
            ) : (
              <>
                <div className="flex items-end justify-between h-48 px-4 mb-4">
                  {headcountTrends.map((item) => {
                    const height = ((item.totalEmployees - minEmployees) / range) * 100;
                    return (
                      <div key={item.month} className="flex flex-col items-center flex-1">
                        <span className="text-xs text-gray-500 mb-1">{item.totalEmployees}</span>
                        <div
                          className="w-12 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-md"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                        <span className="text-xs text-gray-600 mt-2">{item.month}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.currentHeadcount')}</p>
                        <p className="text-2xl font-bold text-blue-600">{latestMonth?.totalEmployees ?? 0}</p>
                      </div>
                      <Users className="w-6 h-6 text-blue-500" />
                    </div>
                    {latestMonth && latestMonth.netChange !== 0 && (
                      <p className={`text-sm mt-2 ${latestMonth.netChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {latestMonth.netChange > 0 ? '+' : ''}{latestMonth.netChange} {t('hrAnalytics.vsLastMonth')}
                      </p>
                    )}
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.newHires')}</p>
                        <p className="text-2xl font-bold text-green-600">{latestMonth?.newHires ?? 0}</p>
                      </div>
                      <UserPlus className="w-6 h-6 text-green-500" />
                    </div>
                    <p className="text-sm text-green-600 mt-2">{t('hrAnalytics.thisMonth')}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.departures')}</p>
                        <p className="text-2xl font-bold text-red-600">{latestMonth?.departures ?? 0}</p>
                      </div>
                      <UserMinus className="w-6 h-6 text-red-500" />
                    </div>
                    <p className="text-sm text-red-600 mt-2">{t('hrAnalytics.thisMonth')}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      }

      case 'turnover': {
        if (loadingTurnover) return <LoadingState title={t('hrAnalytics.loading')} size="md" />;
        const totalDepartures = turnoverData.reduce((sum, d) => sum + d.departures, 0);
        const totalEmps = turnoverData.reduce((sum, d) => sum + d.totalEmployees, 0);
        const overallTurnover = totalEmps > 0 ? ((totalDepartures / totalEmps) * 100).toFixed(1) : '0';
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('hrAnalytics.turnoverByDepartment')}</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">{t('hrAnalytics.departures')}</span>
                </div>
              </div>
            </div>
            {turnoverData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">{t('hrAnalytics.noData')}</div>
            ) : (
              <>
                <div className="space-y-4">
                  {turnoverData.map((item) => (
                    <div key={item.department} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 w-32">{item.department}</span>
                      <div className="flex-1 mx-4">
                        <div className="flex items-center h-8 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-red-500"
                            style={{ width: `${Math.min(item.turnoverRate, 100)}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-bold text-gray-900 w-16 text-end">{item.turnoverRate}%</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.overallTurnover')}</p>
                      <p className="text-2xl font-bold text-gray-900">{overallTurnover}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalDepartures')}</p>
                      <p className="text-2xl font-bold text-red-600">{totalDepartures}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.departmentsTracked')}</p>
                      <p className="text-2xl font-bold text-gray-600">{turnoverData.length}</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      }

      case 'demographics': {
        if (loadingDemographics) return <LoadingState title={t('hrAnalytics.loading')} size="md" />;
        if (!demographics || demographics.totalEmployees === 0) {
          return (
            <div className="bg-card rounded-xl shadow-sm border border-border p-6 text-center py-12 text-gray-500">
              {t('hrAnalytics.noData')}
            </div>
          );
        }
        const distColors = ['bg-green-500', 'bg-blue-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500', 'bg-teal-500', 'bg-pink-500', 'bg-indigo-500'];
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('hrAnalytics.demographics')}</h3>
            </div>
            <div className="space-y-8">
              {/* Gender Distribution */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('hrAnalytics.genderDistribution')}</h4>
                <div className="space-y-3">
                  {demographics.genderDistribution.map((item, idx) => (
                    <div key={item.gender} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${distColors[idx % distColors.length]}`}></div>
                        <span className="font-medium text-gray-900">{t(`hrAnalytics.gender.${item.gender.toLowerCase()}`)}</span>
                      </div>
                      <div className="text-end">
                        <p className="font-semibold text-gray-900">{item.count}</p>
                        <p className="text-sm text-gray-600">{item.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Nationality Distribution */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('hrAnalytics.nationalityDistribution')}</h4>
                <div className="space-y-3">
                  {demographics.nationalityDistribution.map((item, idx) => (
                    <div key={item.nationality} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${distColors[idx % distColors.length]}`}></div>
                        <span className="font-medium text-gray-900">{item.nationality}</span>
                      </div>
                      <div className="text-end">
                        <p className="font-semibold text-gray-900">{item.count}</p>
                        <p className="text-sm text-gray-600">{item.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Age Distribution */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('hrAnalytics.ageDistribution')}</h4>
                <div className="space-y-3">
                  {demographics.ageDistribution.map((item, idx) => (
                    <div key={item.ageGroup} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${distColors[idx % distColors.length]}`}></div>
                        <span className="font-medium text-gray-900">{item.ageGroup}</span>
                      </div>
                      <div className="text-end">
                        <p className="font-semibold text-gray-900">{item.count}</p>
                        <p className="text-sm text-gray-600">{item.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Total */}
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalEmployees')}</p>
                <p className="text-2xl font-bold text-purple-600">{demographics.totalEmployees}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'contracts': {
        if (loadingContracts) return <LoadingState title={t('hrAnalytics.loading')} size="md" />;
        if (!contractData || contractData.byMonth.length === 0) {
          return (
            <div className="bg-card rounded-xl shadow-sm border border-border p-6 text-center py-12 text-gray-500">
              {t('hrAnalytics.noData')}
            </div>
          );
        }
        const maxExpiring = Math.max(...contractData.byMonth.map((m) => m.expiringCount), 1);
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('hrAnalytics.contractExpiry')}</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">{t('hrAnalytics.immediate')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">{t('hrAnalytics.upcoming')}</span>
                </div>
              </div>
            </div>
            <div className="flex items-end justify-between h-48 px-4 mb-4">
              {contractData.byMonth.map((item) => {
                const height = (item.expiringCount / maxExpiring) * 100;
                return (
                  <div key={item.month} className="flex flex-col items-center flex-1">
                    <span className="text-xs text-gray-500 mb-1">{item.expiringCount}</span>
                    <div
                      className={`w-12 rounded-t-md ${item.category === 'immediate' ? 'bg-red-500' : 'bg-orange-500'}`}
                      style={{ height: `${Math.max(height, 5)}%` }}
                    />
                    <span className="text-xs text-gray-600 mt-2">{item.month}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.expiring30Days')}</p>
                <p className="text-2xl font-bold text-red-600">{contractData.expiring30Days}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.expiring60Days')}</p>
                <p className="text-2xl font-bold text-orange-600">{contractData.expiring60Days}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalUpcoming')}</p>
                <p className="text-2xl font-bold text-blue-600">{contractData.totalUpcoming}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'documents': {
        if (loadingDocuments) return <LoadingState title={t('hrAnalytics.loading')} size="md" />;
        if (!documentData || documentData.byType.length === 0) {
          return (
            <div className="bg-card rounded-xl shadow-sm border border-border p-6 text-center py-12 text-gray-500">
              {t('hrAnalytics.noData')}
            </div>
          );
        }
        const maxExpiringSoon = Math.max(...documentData.byType.map((d) => d.expiringSoon), 1);
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('hrAnalytics.documentExpiry')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.documentType')}</th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.expiringSoon')}</th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.urgent30Days')}</th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.expired')}</th>
                    <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.status')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documentData.byType.map((item) => (
                    <tr key={item.documentType}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.documentType}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.expiringSoon}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {item.urgent}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {item.expired}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-pink-500 h-2 rounded-full"
                            style={{ width: `${(item.expiringSoon / maxExpiringSoon) * 100}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalExpiringSoon')}</p>
                <p className="text-2xl font-bold text-orange-600">{documentData.totalExpiringSoon}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalUrgent')}</p>
                <p className="text-2xl font-bold text-red-600">{documentData.totalUrgent}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalExpired')}</p>
                <p className="text-2xl font-bold text-gray-600">{documentData.totalExpired}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'probation': {
        if (loadingProbation) return <LoadingState title={t('hrAnalytics.loading')} size="md" />;
        if (!probationData) {
          return (
            <div className="bg-card rounded-xl shadow-sm border border-border p-6 text-center py-12 text-gray-500">
              {t('hrAnalytics.noData')}
            </div>
          );
        }
        const probItems = [
          { label: t('hrAnalytics.inProbation'), count: probationData.inProbationCount, color: 'bg-indigo-500', pct: probationData.totalActiveCount > 0 ? Math.round((probationData.inProbationCount / probationData.totalActiveCount) * 100) : 0 },
          { label: t('hrAnalytics.endingThisMonth'), count: probationData.endingThisMonthCount, color: 'bg-orange-500', pct: probationData.totalActiveCount > 0 ? Math.round((probationData.endingThisMonthCount / probationData.totalActiveCount) * 100) : 0 },
          { label: t('hrAnalytics.completed'), count: probationData.completedCount, color: 'bg-green-500', pct: probationData.totalActiveCount > 0 ? Math.round((probationData.completedCount / probationData.totalActiveCount) * 100) : 0 },
        ];
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('hrAnalytics.probationOverview')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {probItems.map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-sm font-medium text-gray-600 mb-2">{item.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mb-2">{item.count}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${item.color}`}
                      style={{ width: `${item.pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{item.pct}%</p>
                </div>
              ))}
            </div>
            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalActiveEmployees')}</p>
                  <p className="text-lg font-bold text-indigo-600">{probationData.totalActiveCount}</p>
                </div>
                <Clock className="w-8 h-8 text-indigo-500" />
              </div>
            </div>
          </div>
        );
      }

      case 'assets': {
        if (loadingAssets) return <LoadingState title={t('hrAnalytics.loading')} size="md" />;
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('hrAnalytics.assetAllocation')}</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">{t('hrAnalytics.allocated')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">{t('hrAnalytics.available')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">{t('hrAnalytics.maintenance')}</span>
                </div>
              </div>
            </div>
            {assetData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">{t('hrAnalytics.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.assetCategory')}</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.allocated')}</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.available')}</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.maintenance')}</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hrAnalytics.utilization')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assetData.map((asset) => {
                      const activeTotal = asset.allocated + asset.available + asset.underMaintenance;
                      const utilization = activeTotal > 0 ? ((asset.allocated / activeTotal) * 100).toFixed(1) : '0';
                      return (
                        <tr key={asset.category}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{asset.category}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {asset.allocated}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {asset.available}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              {asset.underMaintenance}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2 me-2">
                                <div
                                  className="bg-teal-600 h-2 rounded-full"
                                  style={{ width: `${utilization}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-900">{utilization}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      }

      default:
        return (
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('hrAnalytics.selectReport')}</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* Page Header */}
      <div className="bg-card rounded-2xl shadow-sm border border-border p-8 mb-6">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg me-4">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('hrAnalytics.title')}</h1>
            <p className="text-gray-600">
              {t('hrAnalytics.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('hrAnalytics.dateRange')}</label>
              <select
                className="border border-border rounded-lg px-3 py-2 text-sm min-w-[180px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                value={selectedMonths}
                onChange={(e) => setSelectedMonths(Number(e.target.value))}
                data-testid="filter-date-range"
              >
                <option value={1}>{t('hrAnalytics.currentMonth')}</option>
                <option value={3}>{t('hrAnalytics.last3Months')}</option>
                <option value={6}>{t('hrAnalytics.last6Months')}</option>
                <option value={12}>{t('hrAnalytics.currentYear')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('hrAnalytics.department')}</label>
              <select
                className="border border-border rounded-lg px-3 py-2 text-sm min-w-[180px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                value={selectedDepartmentId ?? ''}
                onChange={(e) => setSelectedDepartmentId(e.target.value ? Number(e.target.value) : null)}
                data-testid="filter-department"
              >
                <option value="">{t('hrAnalytics.allDepartments')}</option>
                {companyDepartments.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleExport('pdf')}
              disabled={isExporting}
              className="flex items-center gap-2 px-4 py-2 border border-border text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {t('hrAnalytics.exportPdf')}
            </button>
            <button
              onClick={() => handleExport('xlsx')}
              disabled={isExporting}
              className="flex items-center gap-2 px-4 py-2 border border-border text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {t('hrAnalytics.exportExcel')}
            </button>
            <button
              onClick={refreshAll}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
              data-testid="refresh-data-btn"
            >
              <RefreshCw className="w-4 h-4" />
              {t('hrAnalytics.refreshData')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-8">
        {/* Report Categories Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('hrAnalytics.reportCategories')}</h3>
            <div className="space-y-2">
              {reportTypes.map((report) => (
                <button
                  key={report.id}
                  onClick={() => setSelectedReport(report.id)}
                  className={`w-full text-start p-3 rounded-lg transition-all ${
                    selectedReport === report.id
                      ? `${report.bgColor} ${report.color} border border-current`
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={selectedReport === report.id ? report.color : 'text-gray-500'}>
                      {report.icon}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{t(report.title)}</p>
                      <p className="text-xs text-gray-600 mt-1">{t(report.description)}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chart and Analysis */}
        <div className="lg:col-span-3">
          {renderReportContent()}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.totalEmployees')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {headcountTrends.length > 0 ? headcountTrends[headcountTrends.length - 1].totalEmployees : '—'}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.newHires')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {headcountTrends.length > 0 ? headcountTrends[headcountTrends.length - 1].newHires : '—'}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">{t('hrAnalytics.thisMonth')}</p>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.departures')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {headcountTrends.length > 0 ? headcountTrends[headcountTrends.length - 1].departures : '—'}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <UserMinus className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-sm text-red-600 mt-2">{t('hrAnalytics.thisMonth')}</p>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('hrAnalytics.netChange')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {headcountTrends.length > 0 ? headcountTrends[headcountTrends.length - 1].netChange : '—'}
              </p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
            <span className="text-sm font-medium text-gray-700">{t('hrAnalytics.exporting')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
